import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { ProjectsTable, ProjectWithStats } from '@/components/projects/ProjectsTable';
import { ProjectsCardGrid } from '@/components/projects/ProjectCard';
import { ViewToggle, useViewPreference } from '@/components/ui/view-toggle';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { mockTasks, mockTimeEntries } from '@/mocks/data';
import { projectsService, Project } from '@/services/projects.service';
import { clientsService } from '@/services/clients.service';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { useToast } from '@/components/ui/Toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, FolderOpen, Search, X } from 'lucide-react';
import type { Client } from '@/types';

function ProjectsContent() {
  const { toast } = useToast();
  const [view, setView] = useViewPreference('projects-view', 'table');
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteProject, setDeleteProject] = useState<ProjectWithStats | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [projectsData, clientsData] = await Promise.all([
        projectsService.getProjects(),
        clientsService.getClients(),
      ]);
      setProjects(projectsData);
      setClients(clientsData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteProject) return;
    try {
      setIsDeleting(true);
      await projectsService.deleteProject(deleteProject.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteProject.id));
      toast.success('Project deleted successfully');
    } catch (err: any) {
      console.error('Error deleting project:', err);
      toast.error(err.message || 'Failed to delete project');
    } finally {
      setIsDeleting(false);
      setDeleteProject(null);
    }
  };

  // Calculate project stats
  const projectsWithStats: ProjectWithStats[] = useMemo(() => {
    return projects.map((project) => {
      const client = clients.find((c) => c.id === project.clientId);
      const projectTasks = mockTasks.filter((task) => task.projectId === project.id);
      const completedTasks = projectTasks.filter((task) => task.status === 'completed');
      const timeEntries = mockTimeEntries.filter((entry) => entry.projectId === project.id);
      const totalHours = timeEntries.reduce((total, entry) => total + entry.duration / 60, 0);
      const earnings = totalHours * (client?.hourlyRate || 0);

      return {
        ...project,
        createdAt: project.createdAt || new Date().toISOString(),
        clientName: client?.name,
        totalTasks: projectTasks.length,
        completedTasks: completedTasks.length,
        totalHours: Math.round(totalHours * 10) / 10,
        earnings: Math.round(earnings),
        status: (projectTasks.length === 0
          ? 'not-started'
          : completedTasks.length === projectTasks.length
            ? 'completed'
            : 'in-progress') as 'not-started' | 'in-progress' | 'completed',
      };
    });
  }, [projects, clients]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projectsWithStats.filter((project) => {
      const matchesSearch =
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.clientName && project.clientName.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesClient = clientFilter === 'all' || project.clientId === clientFilter;
      const matchesType = typeFilter === 'all' || project.type === typeFilter;

      return matchesSearch && matchesClient && matchesType;
    });
  }, [projectsWithStats, searchTerm, clientFilter, typeFilter]);

  const activeFiltersCount = [clientFilter !== 'all', typeFilter !== 'all'].filter(Boolean).length;

  const clearAllFilters = () => {
    setClientFilter('all');
    setTypeFilter('all');
  };

  const emptyState = (
    <div className="text-center py-12 border border-border rounded-lg">
      <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {searchTerm || clientFilter !== 'all' || typeFilter !== 'all'
          ? 'No projects found'
          : 'No projects yet'}
      </h3>
      <p className="text-muted-foreground mb-4">
        {searchTerm || clientFilter !== 'all' || typeFilter !== 'all'
          ? 'Try adjusting your search criteria or filters'
          : 'Get started by creating your first project'}
      </p>
      {!searchTerm && clientFilter === 'all' && typeFilter === 'all' && (
        <Button asChild>
          <Link to="/projects/new">
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Link>
        </Button>
      )}
    </div>
  );

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Projects' }]} />

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96" />
        </div>
      ) : (
        <>
          {/* Toolbar - Inline search and filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Client Filter */}
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="client">Client Projects</SelectItem>
                <SelectItem value="side-project">Side Projects</SelectItem>
              </SelectContent>
            </Select>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Add Button */}
            <Button asChild>
              <Link to="/projects/new">
                <Plus className="w-4 h-4" />
              </Link>
            </Button>

            {/* View Toggle */}
            <ViewToggle view={view} onViewChange={setView} onRefresh={loadData} />
          </div>

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>

              {clientFilter !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setClientFilter('all')}
                >
                  Client: {clients.find((c) => c.id === clientFilter)?.name}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {typeFilter !== 'all' && (
                <Badge
                  variant="secondary"
                  className="cursor-pointer"
                  onClick={() => setTypeFilter('all')}
                >
                  Type: {typeFilter === 'client' ? 'Client Projects' : 'Side Projects'}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              )}

              {activeFiltersCount > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </Button>
              )}
            </div>
          )}

          {/* Content */}
          {view === 'table' ? (
            <ProjectsTable
              projects={filteredProjects}
              isLoading={loading}
              onDelete={setDeleteProject}
              emptyState={emptyState}
            />
          ) : (
            <ProjectsCardGrid projects={filteredProjects} emptyState={emptyState} />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteProject}
        onClose={() => setDeleteProject(null)}
        onConfirm={handleDelete}
        title="Delete Project"
        description={`Are you sure you want to delete "${deleteProject?.name}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </AppLayout>
  );
}

export default function ProjectsPage() {
  return (
    <ProtectedRoute>
      <ProjectsContent />
    </ProtectedRoute>
  );
}
